import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import { startOfDay, endOfDay } from 'date-fns';
import { useQueryClient } from 'react-query';
import { clearSeconds, createDateWithTimeZoneOffset } from '@dispatch/shared';
import {
  useUpdateEventResourceAndTime,
  useUpsertEvent,
  useDeleteEvent,
  useRescheduleTask,
} from '@dispatch/shared/hooks';
import { useNotification } from '@dispatch/web/ui';
import {
  UpdateEventAssignmentAndTimeParams,
  UpsertEventParams,
  TaskRescheduleRequest,
} from '@dispatch/shared/types';
import { RescheduleModalState } from '../types/scheduler.types';
import { mapDate } from '../helpers/mapDate';
import { useAsyncMutation } from './useAsyncMutation';

interface UseEventHandlersOptions {
  timeZone: string;
  tenantId: string;
  onSuccess?: () => void;
}

interface EventUpdateParams {
  eventId: string;
  oldResourceId: string;
  newResourceId: string;
  startDate: string;
  endDate: string;
  rescheduleReasonCode?: string;
  event?: unknown;
}

/**
 * Hook to handle all scheduler event operations
 *
 * Provides:
 * - Event update (drag/drop, resize)
 * - Event creation
 * - Event deletion
 * - Task scheduling (from unassigned grid)
 * - Reschedule reason modal state
 *
 * Uses useAsyncMutation to bridge callback-based shared mutations
 * with promise-based APIs required by Bryntum's async handlers.
 */
export function useEventHandlers({
  timeZone,
  tenantId,
  onSuccess,
}: UseEventHandlersOptions) {
  const { notifySuccess, notifyError } = useNotification();
  const queryClient = useQueryClient();

  // Reschedule reason modal state
  const [rescheduleModal, setRescheduleModal] = useState<RescheduleModalState>({
    open: false,
  });
  // Ref to avoid stale closure in closeRescheduleModal
  const rescheduleResetRef = useRef<(() => void) | undefined>();

  // V1 parity: after any mutation failure we refetch schedule data so the
  // store snaps back to server truth. Without this a failed update leaves
  // the optimistic Bryntum record on screen until the user shifts or
  // refreshes manually.
  const invalidateScheduleData = useCallback(() => {
    queryClient.invalidateQueries(['schedule-data']);
    queryClient.invalidateQueries(['schedule-data-infinite']);
  }, [queryClient]);

  // Async mutation wrappers - each bridges callbacks to promises
  const updateMutation = useAsyncMutation<UpdateEventAssignmentAndTimeParams>({
    onSuccess: () => {
      notifySuccess('Event Updated', 'Event has been successfully updated');
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('Update Failed', error.message || 'Failed to update event');
      invalidateScheduleData();
    },
  });

  const createMutation = useAsyncMutation<UpsertEventParams>({
    onSuccess: () => {
      notifySuccess('Event Created', 'Event has been successfully created');
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('Creation Failed', error.message || 'Failed to create event');
      invalidateScheduleData();
    },
  });

  const deleteMutation = useAsyncMutation<{
    eventId: string;
    resourceId: string;
  }>({
    onSuccess: () => {
      notifySuccess('Event Deleted', 'Event has been successfully deleted');
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('Deletion Failed', error.message || 'Failed to delete event');
      invalidateScheduleData();
    },
  });

  const scheduleMutation = useAsyncMutation<{
    taskId: string;
    request: TaskRescheduleRequest;
  }>({
    onSuccess: () => {
      notifySuccess('Task Scheduled', 'Task has been successfully scheduled');
      onSuccess?.();
    },
    onError: (error) => {
      notifyError(
        'Scheduling Failed',
        error.message || 'Failed to schedule task'
      );
      // V1 parity: drop any pending highlight/pending-event state by forcing
      // a refetch; the stores realign once the real data arrives.
      invalidateScheduleData();
    },
  });

  const { updateEvent } = useUpdateEventResourceAndTime(
    () => updateMutation.onMutationSuccess(),
    (errorResponse) =>
      updateMutation.onMutationError(new Error(errorResponse.message))
  );

  const { upsertEvent } = useUpsertEvent(
    (response: { success: boolean; message?: string }) => {
      if (response?.success) {
        createMutation.onMutationSuccess();
      } else {
        const errorMessage =
          response?.message === 'Conflict'
            ? 'There was a scheduling conflict'
            : 'Failed to create event';
        createMutation.onMutationError(new Error(errorMessage));
      }
    },
    () => createMutation.onMutationError(new Error('Failed to create event'))
  );

  const { deleteEvent } = useDeleteEvent((response) => {
    if (response.success) {
      deleteMutation.onMutationSuccess();
    } else {
      deleteMutation.onMutationError(new Error('Failed to delete event'));
    }
  });

  const rescheduleTaskMutation = useRescheduleTask(
    () => scheduleMutation.onMutationSuccess(),
    (errorResponse) =>
      scheduleMutation.onMutationError(new Error(errorResponse.message))
  );

  /**
   * Handle event update (drag/drop or resize)
   */
  const handleEventUpdate = useCallback(
    async (params: EventUpdateParams): Promise<void> => {
      const updateParams: UpdateEventAssignmentAndTimeParams = {
        eventId: params.eventId,
        oldResourceId: params.oldResourceId,
        newResourceId: params.newResourceId,
        startDateTime: params.startDate,
        endDateTime: params.endDate,
        reasonCode: params.rescheduleReasonCode || '',
        event: params.event,
      };

      const promise = updateMutation.mutateAsync(updateParams);
      updateEvent(updateParams);
      return promise;
    },
    [updateEvent, updateMutation]
  );

  /**
   * Handle event update with reschedule reason modal
   */
  const handleEventUpdateWithReason = useCallback(
    (params: Omit<EventUpdateParams, 'rescheduleReasonCode'>) => {
      return new Promise<void>((resolve, reject) => {
        const reset = () => {
          setRescheduleModal({ open: false });
          reject(new Error('Cancelled by user'));
        };
        rescheduleResetRef.current = reset;
        setRescheduleModal({
          open: true,
          callback: async (reasonCode: string) => {
            try {
              await handleEventUpdate({
                ...params,
                rescheduleReasonCode: reasonCode,
              });
              setRescheduleModal({ open: false });
              rescheduleResetRef.current = undefined;
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          reset,
        });
      });
    },
    [handleEventUpdate]
  );

  /**
   * Input type for creating/updating blocked time events
   * Note: Uses simple object type to avoid SchedulerEvent intersection issues
   */
  type CreateEventInput = {
    resourceId?: string;
    name?: string;
    eventName?: string;
    startDate?: Date | string;
    endDate?: Date | string;
    notes?: string | null;
    category?: string | null;
    externalId?: string;
    isForced?: boolean;
    isAllDay?: boolean;
  };

  /**
   * Handle event creation (also used for updating blocked time events via upsert)
   * Transforms Bryntum event model to UpsertEventParams for API
   *
   * Note: For existing events, pass externalId to update instead of create
   */
  const handleEventCreate = useCallback(
    async (event: CreateEventInput): Promise<void> => {
      const rawStart = event.startDate ? mapDate(event.startDate) : new Date();
      const rawEnd = event.endDate ? mapDate(event.endDate) : new Date();

      // V1 parity: clear seconds on both all-day and timed events so saved
      // times round to whole minutes. For all-day we first pin to start/end of
      // day in local time before the zone offset conversion.
      const baseStart = event.isAllDay ? startOfDay(rawStart) : rawStart;
      const baseEnd = event.isAllDay ? endOfDay(rawEnd) : rawEnd;
      const [adjustedStart, adjustedEnd] = clearSeconds(
        createDateWithTimeZoneOffset(baseStart, timeZone),
        createDateWithTimeZoneOffset(baseEnd, timeZone)
      );

      // V1 parity: reject end-before-start with a toast and refetch so the
      // scheduler store is realigned to the server truth.
      if (adjustedEnd.getTime() < adjustedStart.getTime()) {
        notifyError('Validation Error', 'Error creating event.');
        invalidateScheduleData();
        throw new Error('End date must be on or after the start date');
      }

      // V1 parity: regenerate the id when the caller passes a malformed UUID,
      // not only when it's missing.
      const externalId =
        event.externalId && uuidValidate(event.externalId)
          ? event.externalId
          : uuidv4();

      const upsertParams: UpsertEventParams = {
        resourceId: event.resourceId || '',
        externalId,
        tenantId: tenantId,
        eventName: event.name || event.eventName || '',
        source: 'scheduler',
        startDateTime: adjustedStart.toISOString(),
        endDateTime: adjustedEnd.toISOString(),
        timeZoneId: timeZone,
        TaskReferenceId: '',
        category: event.category || null,
        notes: event.notes || null,
        isForced: event.isForced,
        isAllDay: event.isAllDay,
      };

      const promise = createMutation.mutateAsync(upsertParams);
      upsertEvent(upsertParams);
      return promise;
    },
    [
      upsertEvent,
      timeZone,
      tenantId,
      createMutation,
      notifyError,
      invalidateScheduleData,
    ]
  );

  /**
   * Handle event deletion
   */
  const handleEventDelete = useCallback(
    async (eventId: string, resourceId: string): Promise<void> => {
      const params = { eventId, resourceId };
      const promise = deleteMutation.mutateAsync(params);
      deleteEvent(params);
      return promise;
    },
    [deleteEvent, deleteMutation]
  );

  /**
   * Handle scheduling a task from the unassigned grid
   */
  const handleTaskSchedule = useCallback(
    async (
      taskId: string,
      params: {
        resourceId: string;
        startDate: string;
        endDate: string;
        rescheduleReasonCode?: string;
        isPrecise?: boolean;
      }
    ): Promise<void> => {
      const request: TaskRescheduleRequest = {
        resourceId: params.resourceId,
        appointmentStart: params.startDate,
        appointmentEnd: params.endDate,
        reasonCode: params.rescheduleReasonCode || '',
        isPrecise: params.isPrecise,
      };

      const mutationParams = { taskId, request };
      const promise = scheduleMutation.mutateAsync(mutationParams);
      rescheduleTaskMutation.mutate(mutationParams);
      return promise;
    },
    [rescheduleTaskMutation, scheduleMutation]
  );

  /**
   * Close reschedule modal without action
   */
  const closeRescheduleModal = useCallback(() => {
    rescheduleResetRef.current?.();
  }, []);

  return {
    // Event handlers
    handleEventUpdate,
    handleEventUpdateWithReason,
    handleEventCreate,
    handleEventDelete,
    handleTaskSchedule,

    // Modal state
    rescheduleModal,
    closeRescheduleModal,

    // Loading states
    isUpdating: updateMutation.isLoading,
    isCreating: createMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    isScheduling: scheduleMutation.isLoading,
    isProcessing:
      updateMutation.isLoading ||
      createMutation.isLoading ||
      deleteMutation.isLoading ||
      scheduleMutation.isLoading,
  };
}
